import { hasStaticFeedback } from "@memorang/helpers/feedback";
import { formatList } from "@memorang/helpers/i18n";
import {
	isAnswered,
	isAnsweredCorrectly,
	isItemReviewable,
} from "@memorang/helpers/item";
import { isAiSubmittable } from "@memorang/helpers/session";
import {
	isProficiencyTag,
	isQuestionTypeTag,
	isSkillTag,
} from "@memorang/helpers/tag";
import type {
	BaseItemSchema,
	MixedItemTag,
	OriginalItemTypes,
} from "@memorang/types";
import type { TFunction } from "i18next";
import { questionTypeIconMap, skillIconMap } from "../../../constants";
import type { LevelIconName } from "../../../icon";
import type {
	BuildReviewAnswersContextOptions,
	ReviewAnswersAction,
	ReviewAnswersBasePerformanceOption,
	ReviewAnswersBaseTagOption,
	ReviewAnswersContext,
	ReviewAnswersFilterChange,
	ReviewAnswersPerformanceId,
	ReviewAnswersSelection,
	ReviewAnswersState,
	ReviewAnswersToggleAction,
	ReviewAnswersView,
} from "./types";

const compareOptions = (
	a: { id: number | string; label: string; order: number },
	b: { id: number | string; label: string; order: number },
) => {
	const orderDifference = a.order - b.order;
	if (orderDifference !== 0) {
		return orderDifference;
	}

	const labelDifference = a.label.localeCompare(b.label);
	if (labelDifference !== 0) {
		return labelDifference;
	}

	return String(a.id).localeCompare(String(b.id));
};

const uniqueTags = (tags: MixedItemTag[]) =>
	tags.filter(
		(tag, index) =>
			tags.findIndex((candidate) => candidate.id === tag.id) === index,
	);

const buildTagOptions = ({
	items,
	matches,
	iconMap,
	orderBy = "sortOrder",
}: {
	items: BaseItemSchema[];
	matches: (tag: MixedItemTag) => boolean;
	iconMap?: Record<string, LevelIconName>;
	orderBy?: "sortOrder" | "firstAppearance";
}): ReviewAnswersBaseTagOption[] =>
	uniqueTags(items.flatMap((item) => item.tags?.filter(matches) ?? []))
		.map((tag, index) => ({
			id: tag.id,
			label: tag.title,
			icon: iconMap?.[tag.title],
			order:
				orderBy === "firstAppearance"
					? index
					: (tag.sortOrder ?? Number.MAX_SAFE_INTEGER),
		}))
		.sort(compareOptions);

/**
 * AI-scored items (TOEIC speaking/writing, PTE assessor items, etc.) are graded
 * on a rubric rather than a right/wrong answer key, so the dialog swaps the
 * Correct/Incorrect rows for a single Answered row for the whole session.
 */
const isAiScoredItem = (item: BaseItemSchema) =>
	isAiSubmittable(
		item.response?.type,
		item.metadata?.type as OriginalItemTypes | undefined,
		item.metadata?.toeic_artifact_type,
		hasStaticFeedback(item.feedback),
	);

const buildPerformanceOptions = ({
	hasAiScoring,
	t,
}: {
	hasAiScoring: boolean;
	t: TFunction;
}): ReviewAnswersBasePerformanceOption[] =>
	hasAiScoring
		? [
				{
					id: "answered",
					label: t("common.answered"),
					icon: "check-circle-outline",
					order: 0,
				},
				{
					id: "skipped",
					label: t("common.skipped"),
					icon: "minus-circle-outline",
					order: 1,
				},
			]
		: [
				{
					id: "correct",
					label: t("common.correct"),
					icon: "check-circle-outline",
					order: 0,
				},
				{
					id: "incorrect",
					label: t("common.incorrect"),
					icon: "close-circle-outline",
					order: 1,
				},
				{
					id: "skipped",
					label: t("common.skipped"),
					icon: "minus-circle-outline",
					order: 2,
				},
			];

const classifyItemPerformance = ({
	item,
	hasAiScoring,
}: {
	item: BaseItemSchema;
	hasAiScoring: boolean;
}): ReviewAnswersPerformanceId[] => {
	const answer = item.answer;
	if (answer == null) {
		return [];
	}

	if (!isAnswered(answer)) {
		return ["skipped"];
	}

	if (hasAiScoring) {
		return ["answered"];
	}

	return isAnsweredCorrectly(answer) ? ["correct"] : ["incorrect"];
};

const getTagIds = (
	item: BaseItemSchema,
	matches: (tag: MixedItemTag) => boolean,
) => uniqueTags(item.tags?.filter(matches) ?? []).map((tag) => tag.id);

export const buildReviewAnswersContext = (
	options: BuildReviewAnswersContextOptions,
): ReviewAnswersContext => {
	const items = options.sessionItems.filter((item) => isItemReviewable(item));
	const hasAiScoring = items.some(isAiScoredItem);
	const performance = buildPerformanceOptions({ hasAiScoring, t: options.t });

	return {
		items: items.map((item) => ({
			item,
			skillIds: getTagIds(item, isSkillTag),
			questionTypeIds: getTagIds(item, isQuestionTypeTag),
			proficiencyIds: getTagIds(item, isProficiencyTag),
			performanceIds: classifyItemPerformance({
				item,
				hasAiScoring,
			}),
		})),
		options: {
			skills: buildTagOptions({
				items,
				matches: isSkillTag,
				iconMap: skillIconMap,
			}),
			questionTypes: buildTagOptions({
				items,
				matches: isQuestionTypeTag,
				iconMap: questionTypeIconMap,
				orderBy: "firstAppearance",
			}),
			proficiencies: buildTagOptions({
				items,
				matches: isProficiencyTag,
				iconMap: options.proficiencyIconMap,
			}),
			performance,
		},
	};
};

const includesAll = <T>(selected: T[], available: T[]) =>
	available.every((value) => selected.includes(value));

const matchesFacet = <T>(itemValues: T[], selected: T[], available: T[]) =>
	includesAll(selected, available) ||
	itemValues.some((value) => selected.includes(value));

const toggleValue = <T>(values: T[], value: T): T[] =>
	values.includes(value)
		? values.filter((candidate) => candidate !== value)
		: [...values, value];

const getPositivePerformanceIds = (context: ReviewAnswersContext) =>
	context.options.performance
		.filter((option) =>
			context.items.some((item) => item.performanceIds.includes(option.id)),
		)
		.map((option) => option.id);

const getVisibleQuestionTypeIds = (
	context: ReviewAnswersContext,
	skillIds: number[],
) => {
	const availableSkillIds = context.options.skills.map((option) => option.id);
	return context.options.questionTypes
		.filter((option) =>
			context.items.some(
				(item) =>
					item.questionTypeIds.includes(option.id) &&
					matchesFacet(item.skillIds, skillIds, availableSkillIds),
			),
		)
		.map((option) => option.id);
};

const countMatchingItems = ({
	context,
	selection,
	visibleQuestionTypeIds,
	performanceIds,
	matchesOption,
	ignoredFacets,
}: {
	context: ReviewAnswersContext;
	selection: ReviewAnswersSelection;
	visibleQuestionTypeIds: number[];
	performanceIds: ReviewAnswersPerformanceId[];
	matchesOption: (item: ReviewAnswersContext["items"][number]) => boolean;
	ignoredFacets: Array<
		"skill" | "questionType" | "proficiency" | "performance"
	>;
}) => {
	const availableSkillIds = context.options.skills.map((option) => option.id);
	const availableProficiencyIds = context.options.proficiencies.map(
		(option) => option.id,
	);

	return context.items.filter(
		(item) =>
			matchesOption(item) &&
			(ignoredFacets.includes("skill") ||
				matchesFacet(item.skillIds, selection.skillIds, availableSkillIds)) &&
			(ignoredFacets.includes("questionType") ||
				matchesFacet(
					item.questionTypeIds,
					selection.questionTypeIds,
					visibleQuestionTypeIds,
				)) &&
			(ignoredFacets.includes("proficiency") ||
				matchesFacet(
					item.proficiencyIds,
					selection.proficiencyIds,
					availableProficiencyIds,
				)) &&
			(ignoredFacets.includes("performance") ||
				matchesFacet(
					item.performanceIds,
					selection.performanceIds,
					performanceIds,
				)),
	).length;
};

type FacetCounts = {
	skills: Record<number, number>;
	questionTypes: Record<number, number>;
	proficiencies: Record<number, number>;
	performance: { [id in ReviewAnswersPerformanceId]?: number };
};

const deriveCounts = ({
	context,
	selection,
	visibleQuestionTypeIds,
	performanceIds,
}: {
	context: ReviewAnswersContext;
	selection: ReviewAnswersSelection;
	visibleQuestionTypeIds: number[];
	performanceIds: ReviewAnswersPerformanceId[];
}): FacetCounts => {
	const skills: Record<number, number> = {};
	for (const option of context.options.skills) {
		skills[option.id] = countMatchingItems({
			context,
			selection,
			visibleQuestionTypeIds,
			performanceIds,
			matchesOption: (item) => item.skillIds.includes(option.id),
			ignoredFacets: ["skill", "questionType"],
		});
	}

	const questionTypes: Record<number, number> = {};
	for (const option of context.options.questionTypes) {
		questionTypes[option.id] = countMatchingItems({
			context,
			selection,
			visibleQuestionTypeIds,
			performanceIds,
			matchesOption: (item) => item.questionTypeIds.includes(option.id),
			ignoredFacets: ["questionType"],
		});
	}

	const proficiencies: Record<number, number> = {};
	for (const option of context.options.proficiencies) {
		proficiencies[option.id] = countMatchingItems({
			context,
			selection,
			visibleQuestionTypeIds,
			performanceIds,
			matchesOption: (item) => item.proficiencyIds.includes(option.id),
			ignoredFacets: ["proficiency"],
		});
	}

	const performance: { [id in ReviewAnswersPerformanceId]?: number } = {};
	for (const option of context.options.performance) {
		performance[option.id] = countMatchingItems({
			context,
			selection,
			visibleQuestionTypeIds,
			performanceIds,
			matchesOption: (item) => item.performanceIds.includes(option.id),
			ignoredFacets: ["performance"],
		});
	}

	return { skills, questionTypes, proficiencies, performance };
};

const resolveSelectedValues = <T>({
	preferred,
	options,
	count,
}: {
	preferred: T[];
	options: T[];
	count: (id: T) => number;
}) => {
	const positive = options.filter((id) => count(id) > 0);
	const selected = preferred.filter((id) => positive.includes(id));
	return selected.length === 0 && preferred.length > 0 ? positive : selected;
};

type DerivedSelection = {
	selection: ReviewAnswersSelection;
	visibleQuestionTypeIds: number[];
	counts: FacetCounts;
};

const deriveStableSelection = ({
	context,
	state,
}: {
	context: ReviewAnswersContext;
	state: ReviewAnswersState;
}): DerivedSelection => {
	const visibleQuestionTypeIds = getVisibleQuestionTypeIds(
		context,
		state.selection.skillIds,
	);
	const preferredQuestionTypeIds = visibleQuestionTypeIds.filter(
		(id) => state.questionTypePreferences[id] === true,
	);
	const preferred: ReviewAnswersSelection = {
		...state.selection,
		questionTypeIds: preferredQuestionTypeIds,
	};
	const performanceIds = getPositivePerformanceIds(context);
	const counts = deriveCounts({
		context,
		selection: preferred,
		visibleQuestionTypeIds,
		performanceIds,
	});
	const selection: ReviewAnswersSelection = {
		skillIds: resolveSelectedValues({
			preferred: preferred.skillIds,
			options: context.options.skills.map((option) => option.id),
			count: (id) => counts.skills[id] ?? 0,
		}),
		questionTypeIds: resolveSelectedValues({
			preferred: preferred.questionTypeIds,
			options: visibleQuestionTypeIds,
			count: (id) => counts.questionTypes[id] ?? 0,
		}),
		proficiencyIds: resolveSelectedValues({
			preferred: preferred.proficiencyIds,
			options: context.options.proficiencies.map((option) => option.id),
			count: (id) => counts.proficiencies[id] ?? 0,
		}),
		performanceIds: resolveSelectedValues({
			preferred: preferred.performanceIds,
			options: performanceIds,
			count: (id) => counts.performance[id] ?? 0,
		}),
	};

	return { selection, visibleQuestionTypeIds, counts };
};

export const createInitialReviewAnswersState = (
	context: ReviewAnswersContext,
): ReviewAnswersState => {
	const performanceIds = getPositivePerformanceIds(context);
	const questionTypePreferences: Record<number, boolean> = {};
	for (const option of context.options.questionTypes) {
		questionTypePreferences[option.id] = context.items.some((item) =>
			item.questionTypeIds.includes(option.id),
		);
	}

	return {
		selection: {
			skillIds: context.options.skills
				.filter((option) =>
					context.items.some((item) => item.skillIds.includes(option.id)),
				)
				.map((option) => option.id),
			questionTypeIds: context.options.questionTypes
				.filter((option) => questionTypePreferences[option.id] === true)
				.map((option) => option.id),
			proficiencyIds: context.options.proficiencies
				.filter((option) =>
					context.items.some((item) => item.proficiencyIds.includes(option.id)),
				)
				.map((option) => option.id),
			performanceIds,
		},
		questionTypePreferences,
	};
};

const selectionWithAction = ({
	selection,
	action,
}: {
	selection: ReviewAnswersSelection;
	action: ReviewAnswersToggleAction;
}): ReviewAnswersSelection => {
	if (action.facet === "skill") {
		return {
			...selection,
			skillIds: toggleValue(selection.skillIds, action.id),
		};
	}
	if (action.facet === "questionType") {
		return {
			...selection,
			questionTypeIds: toggleValue(selection.questionTypeIds, action.id),
		};
	}
	if (action.facet === "proficiency") {
		return {
			...selection,
			proficiencyIds: toggleValue(selection.proficiencyIds, action.id),
		};
	}
	return {
		...selection,
		performanceIds: toggleValue(selection.performanceIds, action.id),
	};
};

const setAllReviewAnswersState = ({
	context,
	state,
	facet,
	setAll,
}: {
	context: ReviewAnswersContext;
	state: ReviewAnswersState;
	facet: "questionType" | "proficiency" | "performance";
	setAll: boolean;
}): ReviewAnswersState => {
	if (facet === "questionType") {
		const visibleIds = getVisibleQuestionTypeIds(
			context,
			state.selection.skillIds,
		);
		const nextPreferences = { ...state.questionTypePreferences };
		for (const id of visibleIds) {
			nextPreferences[id] = setAll;
		}
		return {
			selection: {
				...state.selection,
				questionTypeIds: setAll ? visibleIds : [],
			},
			questionTypePreferences: nextPreferences,
		};
	}

	if (facet === "proficiency") {
		return {
			...state,
			selection: {
				...state.selection,
				proficiencyIds: setAll
					? context.options.proficiencies.map((option) => option.id)
					: [],
			},
		};
	}

	return {
		...state,
		selection: {
			...state.selection,
			performanceIds: setAll ? getPositivePerformanceIds(context) : [],
		},
	};
};

export const toggleReviewAnswersState = ({
	context,
	state,
	action,
}: {
	context: ReviewAnswersContext;
	state: ReviewAnswersState;
	action: ReviewAnswersAction;
}): ReviewAnswersState => {
	if ("setAll" in action) {
		return setAllReviewAnswersState({
			context,
			state,
			facet: action.facet,
			setAll: action.setAll,
		});
	}

	const nextPreferences = { ...state.questionTypePreferences };
	if (action.facet === "questionType") {
		nextPreferences[action.id] = nextPreferences[action.id] !== true;
	}

	let selection = selectionWithAction({ selection: state.selection, action });
	if (action.facet === "skill") {
		const visibleIds = getVisibleQuestionTypeIds(context, selection.skillIds);
		let questionTypeIds = visibleIds.filter(
			(id) => nextPreferences[id] === true,
		);
		if (
			questionTypeIds.length === 0 &&
			state.selection.questionTypeIds.length > 0 &&
			visibleIds.length > 0
		) {
			const temporaryPreferences = { ...nextPreferences };
			for (const id of visibleIds) {
				temporaryPreferences[id] = true;
			}
			const temporaryState: ReviewAnswersState = {
				selection: { ...selection, questionTypeIds: visibleIds },
				questionTypePreferences: temporaryPreferences,
			};
			questionTypeIds = deriveStableSelection({
				context,
				state: temporaryState,
			}).selection.questionTypeIds;
			for (const id of questionTypeIds) {
				nextPreferences[id] = true;
			}
		}
		selection = { ...selection, questionTypeIds };
	}

	return { selection, questionTypePreferences: nextPreferences };
};

const collectSelectedLabels = ({
	context,
	state,
	selection,
}: {
	context: ReviewAnswersContext;
	state: ReviewAnswersState;
	selection: ReviewAnswersSelection;
}) => {
	const labels: string[] = [];
	const positivePerformanceIds = getPositivePerformanceIds(context);
	const facets = [
		{
			isNarrowed: !includesAll(
				state.selection.skillIds,
				context.options.skills.map((option) => option.id),
			),
			options: context.options.skills,
			selected: selection.skillIds,
		},
		{
			isNarrowed: context.options.questionTypes.some(
				(option) => state.questionTypePreferences[option.id] !== true,
			),
			options: context.options.questionTypes,
			selected: selection.questionTypeIds,
		},
		{
			isNarrowed: !includesAll(
				state.selection.proficiencyIds,
				context.options.proficiencies.map((option) => option.id),
			),
			options: context.options.proficiencies,
			selected: selection.proficiencyIds,
		},
	];
	for (const facet of facets) {
		if (facet.isNarrowed) {
			labels.push(
				...facet.options
					.filter((option) => facet.selected.includes(option.id))
					.map((option) => option.label),
			);
		}
	}

	if (!includesAll(state.selection.performanceIds, positivePerformanceIds)) {
		labels.push(
			...context.options.performance
				.filter((option) => selection.performanceIds.includes(option.id))
				.map((option) => option.label),
		);
	}
	return labels;
};

export const deriveReviewAnswersView = ({
	context,
	state,
}: {
	context: ReviewAnswersContext;
	state: ReviewAnswersState;
}): ReviewAnswersView => {
	const { selection, visibleQuestionTypeIds, counts } = deriveStableSelection({
		context,
		state,
	});
	const positivePerformanceIds = getPositivePerformanceIds(context);
	const selectedItems = context.items
		.filter(
			(item) =>
				matchesFacet(
					item.skillIds,
					selection.skillIds,
					context.options.skills.map((option) => option.id),
				) &&
				matchesFacet(
					item.questionTypeIds,
					selection.questionTypeIds,
					visibleQuestionTypeIds,
				) &&
				matchesFacet(
					item.proficiencyIds,
					selection.proficiencyIds,
					context.options.proficiencies.map((option) => option.id),
				) &&
				matchesFacet(
					item.performanceIds,
					selection.performanceIds,
					positivePerformanceIds,
				),
		)
		.map((entry) => entry.item);
	const selectedLabels = collectSelectedLabels({ context, state, selection });

	return {
		skills: context.options.skills.map((option) => ({
			...option,
			count: counts.skills[option.id] ?? 0,
			checked: selection.skillIds.includes(option.id),
			disabled: (counts.skills[option.id] ?? 0) === 0,
		})),
		questionTypes: context.options.questionTypes
			.filter((option) => visibleQuestionTypeIds.includes(option.id))
			.map((option) => ({
				...option,
				count: counts.questionTypes[option.id] ?? 0,
				checked: selection.questionTypeIds.includes(option.id),
				disabled: (counts.questionTypes[option.id] ?? 0) === 0,
			})),
		proficiencies: context.options.proficiencies.map((option) => ({
			...option,
			count: counts.proficiencies[option.id] ?? 0,
			checked: selection.proficiencyIds.includes(option.id),
			disabled: (counts.proficiencies[option.id] ?? 0) === 0,
		})),
		performance: context.options.performance.map((option) => ({
			...option,
			count: counts.performance[option.id] ?? 0,
			checked: selection.performanceIds.includes(option.id),
			disabled: (counts.performance[option.id] ?? 0) === 0,
		})),
		selectedItems,
		selectedLabels,
		isAllSelected:
			selectedItems.length === context.items.length &&
			selectedLabels.length === 0,
	};
};

const findToggledOption = (
	action: ReviewAnswersToggleAction,
	view: ReviewAnswersView,
): { label: string; checked: boolean } | undefined => {
	switch (action.facet) {
		case "skill":
			return view.skills.find((option) => option.id === action.id);
		case "questionType":
			return view.questionTypes.find((option) => option.id === action.id);
		case "proficiency":
			return view.proficiencies.find((option) => option.id === action.id);
		default:
			return view.performance.find((option) => option.id === action.id);
	}
};

/** Resolves a filter action against the pre-toggle view into a reportable change. */
export const resolveReviewAnswersFilterChange = ({
	action,
	view,
}: {
	action: ReviewAnswersAction;
	view: ReviewAnswersView;
}): ReviewAnswersFilterChange => {
	if ("setAll" in action) {
		return { facet: action.facet, value: "all", checked: action.setAll };
	}

	const option = findToggledOption(action, view);

	return {
		facet: action.facet,
		value: option?.label ?? String(action.id),
		checked: !option?.checked,
	};
};

export const formatFilterLabels = ({
	labels,
	locale,
	t,
}: {
	labels: string[];
	locale: Intl.LocalesArgument;
	t: TFunction;
}) => {
	if (labels.length <= 3) {
		return formatList(locale, labels);
	}

	return formatList(locale, [
		...labels.slice(0, 2),
		t("report.reviewAnswersDialog.more", { count: labels.length - 2 }),
	]);
};
